import { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import { useAlert } from "../../context/AlertContext";
import { updateRefrigerator } from "../../api/refrigeratorApi.js";
import { createRefrigerator } from "../../api/refrigeratorApi.js";
import { getRefrigerators } from "../../api/refrigeratorApi.js";
import { Link, useNavigate, useParams } from "react-router-dom";

const emptyForm = {
    id: null,
    refrigerator: "",
    status: "ACTIVE",
    remarks: "",
    retailer_id: "",
    user_id: "",
    provided_date: "",
};

export default function RefrigeratorsManagement() {
    const [refrigerators, setRefrigerators] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const { retailerId } = useParams();
    const { showAlert } = useAlert();
    const [edit, setEdit] = useState(false);
    const navigate = useNavigate()

    const loadRefrigerators = async () => {
        const { refrigerators } = await getRefrigerators(retailerId || "");
        if (retailerId && refrigerators.length) {
            onEdit(refrigerators[0])
            setEdit(true)
        }
        setRefrigerators(refrigerators || []);
    };

    useEffect(() => {
        loadRefrigerators();
    }, [retailerId]);

    const onChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (edit) {
                await updateRefrigerator(refrigerators?.[0]?.id || "", { ...form, retailer_id: retailerId });
                showAlert({ message: "Refrigerator updated successfully!", type: "success" });
                navigate("/refrigerators")
            } else {
                await createRefrigerator({ ...form, retailer_id: retailerId });
                showAlert({ message: "Refrigerator added successfully!", type: "success" });
            }

            setForm(emptyForm);
            loadRefrigerators();
        } catch (err) {
            showAlert({ message: "Something went wrong!", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const onEdit = (item) => {
        setForm({
            ...item,
            provided_date: item.provided_date?.split("T")[0] || "",
        });
    };

    return (
        <div className="min-h-screen">
            <Navbar />

            <div className="max-w-6xl mx-auto p-6 space-y-6">
                <h2 className="text-xl font-semibold">Refrigerators Management</h2>

                {/* FORM */}
                {retailerId ?
                    < form
                        onSubmit={onSubmit}
                        className="bg-white p-5 rounded-lg shadow-lg space-y-4"
                    >
                        <h3 className="text-lg font-semibold">
                            {form.id ? "Edit Refrigerator" : "Add Refrigerator"}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="number"
                                name="refrigerator"
                                placeholder="Refrigerator Number"
                                value={form.refrigerator}
                                onChange={onChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                            />

                            <input
                                type="number"
                                name="retailer_id"
                                placeholder="Retailer ID"
                                value={retailerId}
                                onChange={onChange}
                                disabled
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                            />
                            <div>
                                <div className="text-md">Provided Date</div>
                                <input
                                    type="date"
                                    name="provided_date"
                                    value={form.provided_date}
                                    onChange={onChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                                />
                            </div>
                        </div>

                        <textarea
                            name="remarks"
                            placeholder="Remarks"
                            value={form.remarks}
                            onChange={onChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />

                        <select
                            name="status"
                            value={form.status}
                            onChange={onChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        >
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                        </select>

                        <div className="flex gap-2">
                            <button
                                disabled={loading}
                                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
                            >
                                {form.id ? "Update" : "Save"}
                            </button>

                            {form.id && (
                                <button
                                    type="button"
                                    onClick={() => setForm(emptyForm)}
                                    className="px-5 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                    : null}
                {/* LIST */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-left">
                            <tr>
                                <th className="p-3">Refrigerator</th>
                                <th>Retailer</th>
                                <th>Provided Date</th>
                                <th>Status</th>
                                <th className="w-20"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {refrigerators?.length > 0 ? (
                                refrigerators?.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="border-t hover:bg-gray-50"
                                    >
                                        <td className="p-3">{r.refrigerator}</td>
                                        <td>{r.name}</td>
                                        <td>{r.provided_date}</td>
                                        <td>
                                            <span
                                                className={`px-2 py-1 rounded text-xs ${r.status === "ACTIVE"
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-gray-200 text-gray-700"
                                                    }`}
                                            >
                                                {r.status}
                                            </span>
                                        </td>
                                        <td>
                                            <Link to={"/refrigerators/" + r.retailer_id}>
                                                <button
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    Edit
                                                </button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="6"
                                        className="p-4 text-center text-gray-400"
                                    >
                                        No refrigerators found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div >
    );
}
